import React from 'react';
import type { EmergencyHealthData } from '../types/emergency';
import './EmergencyDataView.css';

interface EmergencyDataViewProps {
  data: EmergencyHealthData;
  isDemo?: boolean;
}

const EmergencyDataView: React.FC<EmergencyDataViewProps> = ({ data, isDemo = false }) => {
  const getSeverityClass = (severity: string): string => {
    const s = severity.toLowerCase();
    if (s.includes('severe') || s.includes('high') || s.includes('serious')) return 'severe';
    if (s.includes('moderate') || s.includes('medium')) return 'moderate';
    if (s.includes('mild') || s.includes('low') || s.includes('minor')) return 'mild';
    return 'moderate';
  };

  return (
    <div className="emergency-data">
      <div className="emergency-banner">
        <h2>Emergency Health Information</h2>
        <p>Single-use access &mdash; this data is de-identified for patient privacy</p>
      </div>

      {isDemo ? (
        <div className="emergency-demo-banner">
          <strong>DEMO</strong> &mdash; This is a demonstration with sample data. No real patient information is displayed.
        </div>
      ) : (
        <div className="emergency-no-refresh">
          <strong>Do not refresh or close this page.</strong> This is a single-use access code. Refreshing or navigating away will permanently revoke access to this information.
        </div>
      )}

      {/* Patient Demographics */}
      {data.demographics && (data.demographics.gender || data.demographics.ageRange || data.demographics.bloodType) ? (
        <div className="emergency-patient-info">
          <div className="patient-label">Patient Demographics</div>
          <div className="patient-fields">
            {data.demographics.gender && (
              <div className="patient-field">
                <span className="patient-field-label">Gender</span>
                <span className="patient-field-value">{data.demographics.gender}</span>
              </div>
            )}
            {data.demographics.ageRange && (
              <div className="patient-field">
                <span className="patient-field-label">Age Range</span>
                <span className="patient-field-value">{data.demographics.ageRange}</span>
              </div>
            )}
            {data.demographics.bloodType && (
              <div className="patient-field">
                <span className="patient-field-label">Blood Type</span>
                <span className="patient-field-value">{data.demographics.bloodType}</span>
              </div>
            )}
          </div>
        </div>
      ) : data.genderAgeRange ? (
        <div className="emergency-patient-info">
          <div className="patient-label">Patient</div>
          <div className="patient-value">{data.genderAgeRange}</div>
        </div>
      ) : null}

      {/* Allergies — shown first, critical for ER */}
      {data.allergies !== undefined && (
        <div className="emergency-section critical">
          <h3>
            <span className="section-icon" role="img" aria-label="warning">&#9888;</span>
            Allergies
          </h3>
          {data.allergies.length > 0 ? (
            <div className="allergy-list">
              {data.allergies.map((a, i) => (
                <div key={i} className="allergy-item">
                  <span className="allergy-name">{a.allergen}</span>
                  {a.reactionType && <span className="allergy-reaction">{a.reactionType}</span>}
                  {a.severity && (
                    <span className={`severity-badge ${getSeverityClass(a.severity)}`}>
                      {a.severity}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <span className="no-data-text">No known allergies on file</span>
          )}
        </div>
      )}

      {/* Current Medications */}
      {data.medications !== undefined && (
        <div className="emergency-section">
          <h3>
            <span className="section-icon" role="img" aria-label="pill">&#128138;</span>
            Current Medications
          </h3>
          {data.medications.length > 0 ? (
            <div className="medication-list">
              {data.medications.map((m, i) => (
                <div key={i} className="medication-item">
                  <span className="med-name">{m.name}</span>
                  {m.dosageFrequency && <span className="med-dosage">{m.dosageFrequency}</span>}
                </div>
              ))}
            </div>
          ) : (
            <span className="no-data-text">No medications on file</span>
          )}
        </div>
      )}

      {/* Active Conditions */}
      {data.conditions !== undefined && (
        <div className="emergency-section">
          <h3>
            <span className="section-icon" role="img" aria-label="clipboard">&#128203;</span>
            Active Conditions
          </h3>
          {data.conditions.length > 0 ? (
            <div className="simple-list">
              {data.conditions.map((c, i) => (
                <span key={i} className="list-tag">{c}</span>
              ))}
            </div>
          ) : (
            <span className="no-data-text">No active conditions on file</span>
          )}
        </div>
      )}

      {/* Surgical History */}
      {data.surgicalHistory !== undefined && (
        <div className="emergency-section">
          <h3>
            <span className="section-icon" role="img" aria-label="hospital">&#127973;</span>
            Surgical History
          </h3>
          {data.surgicalHistory.length > 0 ? (
            <div className="emergency-table-wrap">
              <table className="emergency-table">
                <thead>
                  <tr>
                    <th>Procedure</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.surgicalHistory.map((s, i) => (
                    <tr key={i}>
                      <td>{s.procedure}</td>
                      <td>{s.date || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <span className="no-data-text">No surgical history on file</span>
          )}
        </div>
      )}

      {/* Vaccine History */}
      {data.vaccineHistory !== undefined && (
        <div className="emergency-section">
          <h3>
            <span className="section-icon" role="img" aria-label="syringe">&#128137;</span>
            Vaccine History
          </h3>
          {data.vaccineHistory.length > 0 ? (
            <div className="emergency-table-wrap">
              <table className="emergency-table">
                <thead>
                  <tr>
                    <th>Vaccine</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.vaccineHistory.map((v, i) => (
                    <tr key={i}>
                      <td>{v.vaccine}</td>
                      <td>{v.date || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <span className="no-data-text">No vaccine history on file</span>
          )}
        </div>
      )}

      {/* Emergency Contacts */}
      {data.emergencyContacts !== undefined && (
        <div className="emergency-section">
          <h3>
            <span className="section-icon" role="img" aria-label="phone">&#128222;</span>
            Emergency Contacts
          </h3>
          {data.emergencyContacts.length > 0 ? (
            <div className="contact-list">
              {data.emergencyContacts.map((c, i) => (
                <div key={i} className="contact-item">
                  <span className="contact-name">{c.name}</span>
                  <span className="contact-relation">{c.relationship}</span>
                  <span className="contact-phone">
                    <a href={`tel:${c.phone}`}>{c.phone}</a>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <span className="no-data-text">No emergency contacts on file</span>
          )}
        </div>
      )}

      {/* Family Doctor */}
      {data.familyDoctor !== undefined && (
        <div className="emergency-section">
          <h3>
            <span className="section-icon" role="img" aria-label="doctor">&#129657;</span>
            Family Doctor
          </h3>
          {data.familyDoctor ? (
            <div className="doctor-info">
              <span className="doctor-name">{data.familyDoctor.name}</span>
              <span className="doctor-phone">
                <a href={`tel:${data.familyDoctor.phone}`}>{data.familyDoctor.phone}</a>
              </span>
            </div>
          ) : (
            <span className="no-data-text">No family doctor on file</span>
          )}
        </div>
      )}

      <div className="emergency-disclaimer">
        Data provided by SilverScribe Inc. &mdash; silverscribe.ca<br />
        This information was current as of the patient's last health summary upload.
      </div>
    </div>
  );
};

export default EmergencyDataView;
